import { ChangeDetectorRef, Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { PageObjectService } from '@Core/models/proprietary/page/page-object.service';
import { AppService } from '@Core/services/proprietary/app.service';

@Component({
    standalone: false,
    selector: 'ep-edit-status-inner',
    template: `
    <div style="display:inline-flex; align-items: center; gap: var(--aurora-spacing-02)">
      <aurora-badge
        *ngIf="layout == 'form' && badgeText && badgeText != 'Unknown'"
        type="text"
        prominence="low"
        class="me-1"
        [color]="getStatusClass(value)"
      >
        {{ badgeText }}
      </aurora-badge>
      <aurora-icon-button
        *ngIf="allowAddAvailable && innerValue == '?' && !app.DesignerMode"
        (click)="addAvailableItem()"
        ngbTooltip="Add"
        icon="plus"
      ></aurora-icon-button>
      <aurora-icon-button *ngIf="allowClone && !app.DesignerMode" (click)="cloneItem()" ngbTooltip="Clone" icon="copy"></aurora-icon-button>
      <aurora-icon-button
        *ngIf="allowDelete && !['D', 'X'].includes(innerValue.toUpperCase()) && !app.DesignerMode"
        (click)="deleteItem()"
        ngbTooltip="Delete"
        icon="trash"
      ></aurora-icon-button>
      <aurora-button
        *ngIf="allowRestore && ['D', 'X'].includes(innerValue.toUpperCase()) && !app.DesignerMode"
        (click)="restoreItem()"
        ngbTooltip="Restore"
        variant="ghost"
      >
        <i class="fa fa-ep-restore"></i>
      </aurora-button>
      <aurora-badge
        *ngIf="layout == 'row' && badgeText && badgeText != 'Unknown'"
        type="text"
        prominence="low"
        class="ms-1"
        [color]="getStatusClass(value)"
      >
        {{ badgeText }}
      </aurora-badge>
    </div>
  `,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => EditStatusInnerComponent),
            multi: true
        }
    ]
})
export class EditStatusInnerComponent implements ControlValueAccessor {
    static key = 'EditStatusInnerComponent';

    @Input('disabled')
    controlDisabled: boolean = false; //is this the right way?

    @Input()
    allowDelete: boolean = false;

    @Input()
    allowRestore: boolean = false;

    @Input()
    allowClone: boolean = false;

    @Input()
    allowAddAvailable: boolean = false;

    @Input()
    layout: string = 'form';

    @Output()
    onClone = new EventEmitter();

    @Output()
    onDelete = new EventEmitter();

    @Output()
    onRestore = new EventEmitter();

    @Output()
    onAddAvailable = new EventEmitter();

    innerValue: string = '';
    badgeText: string = '';

    constructor(private cdRef: ChangeDetectorRef, private pageOS: PageObjectService, public app: AppService) { }

    ngOnInit() { }

    cloneItem() {
        this.onClone.emit();
        this.pageOS.setDirty();
    }

    deleteItem() {
        this.onDelete.emit();
        this.pageOS.setDirty();
    }

    restoreItem() {
        this.onRestore.emit();
        this.pageOS.setDirty();
    }

    addAvailableItem() {
        this.onAddAvailable.emit();
        this.pageOS.setDirty();
    }

    setBadgeText(value: string): void {
        if (value) {
            switch (value.toString().toUpperCase()) {
                case 'N':
                    this.badgeText = null;
                    break;
                case 'A':
                    this.badgeText = 'Added';
                    break;
                case 'D':
                case 'X':
                    this.badgeText = 'Deleted';
                    break;
                case 'C':
                    this.badgeText = 'Changed';
                    break;
                case 'R':
                    this.badgeText = 'Restored';
                    break;
                case '?':
                    this.badgeText = 'Available';
                    break;
                default:
                    this.badgeText = 'Unknown';
                    break;
            }
        }
    }

    getStatusClass(value: any) {
        if (value) {
            switch (value.toString().toUpperCase()) {
                case 'A':
                case 'R':
                    return 'success';
                case '?':
                    return 'info';
                case 'C':
                    return 'warning';
                case 'D':
                case 'X':
                    return 'error';
                default:
                    return;
            }
        }
    }

    //***** BEGIN CONTROL VALUE ACCESSOR HANDLERS *****//

    //Placeholders for the callbacks which are later provided by the Control Value Accessor
    private onChangeCallback = (_: any) => { };
    private onTouchedCallback = () => { };

    //get accessor
    get value(): any {
        return this.innerValue;
    }

    ////set accessor including call the onchange callback
    set value(v: any) {
        if (v !== this.innerValue) {
            this.innerValue = v;
            this.setBadgeText(v);
            this.onChangeCallback(v);
        }
    }

    //From ControlValueAccessor interface - Allows Angular to update the model
    writeValue(v: string): void {
        if (v !== this.innerValue) {
            this.innerValue = v;
            this.setBadgeText(v);
            this.cdRef.detectChanges(); //force change detection
        }
    }

    //From ControlValueAccessor interface - Allows Angular to register a function to call when the model changes.
    registerOnChange(fn: any) {
        this.onChangeCallback = fn;
    }

    //From ControlValueAccessor interface -  Allows Angular to register a function to call when the input has been touched.
    registerOnTouched(fn: any) {
        this.onTouchedCallback = fn;
    }

    //From ControlValueAccessor interface -  Allows Angular to disable the input.
    setDisabledState(isDisabled: boolean): void {
        this.controlDisabled = isDisabled;
    }

    //***** END CONTROL VALUE ACCESSOR HANDLERS *****//
}
